import { isGetUserFinished } from './modules/getUser';
import { OnErrorCommand } from '../gameClient/clientCommands/onErrorCommand';
import { WebCommands } from '../webApp/webCommands';
import { SetStartDataCommand } from '../gameClient/clientCommands/setStartDataCommand';
import { GameClient } from '../gameClient/gameClient';
import consoleLogger from '../logger/consoleLogger';
import { getUserSuid } from './modules/getUserSuid';
import { getBrowserName } from '../utils/browser';
import { GameStartData } from '../types/client';
import { ClusterConfig, EnrichedClusterConfig } from '../types/server';
import { CurrentUser } from './modules/user/currentUser';

const logger = consoleLogger.getPrefixedCopy('GAME_GENERAL');

export function getEnrichedClusterConfig(): EnrichedClusterConfig {
    const clusterConfig: ClusterConfig = window.app.config.client_vars.cluster;

    return {
        ...clusterConfig,
        game_static_noduplicates: window.app.config.game_static_noduplicates,
        game_static_s3_base_url: window.app.config.cdn.game_static_s3_base_url,
        game_static_strings: window.app.config.game_static_strings,
        game_static_vectors: window.app.config.game_static_vectors,
        mobile_static_url: window.app.config.mobile_static_url,
        unity_assets_base_url: window.app.config.cdn.unity_assets_base_url,
        unity_assets_s3_base_url: window.app.config.cdn.unity_assets_s3_base_url,
        // TODO remove when client removes old assets support
        unity_catalog: window.app.unity_catalog
    };
}

function buildStartData(): Partial<GameStartData> {
    const user = window.app.user;
    const startData: Partial<GameStartData> = {
        browser: getBrowserName(),
        client_id: window.app?.log_cookie ?? (`client_generated_${getUserSuid()}_${Date.now()}`),
        cluster_config: getEnrichedClusterConfig(),
        cluster_name: window.app.config.cluster_name,
        country: window.app.config.country,
        entry_point: window.app.config.current_entry_point,
        first_name: user.first_name,
        hasBookmark: user.hasBookmark,
        isLinked: !CurrentUser.getInstance().getHasGuestAuthMethod(),
        last_name: user.last_name,
        locale: window.app.config.locale,
        os: window.app.config.user_os.name,
        photo: user.photo,
        preloader: '',
        preloader_bg: '',
        preloader_img: '',
        reg_preloader_bg: '',
        reg_preloader_img: '',
        restApiUrl: window.app.restApiUrl,
        sig: window.app.config.sig,
        sig_funnel: window.app.config.sig_funnel,
        suid: getUserSuid(),
        time_from_load_page: (window.app.time_embed_ready - window.app.time_from_load_page),
        user: { result: window.app.getGameStartDataResponse },
        user_agent: window.app.config.user_agent,
        user_currency: window.app.config.user_currency,
        wait_for_web_auth: 1,
        account_token: CurrentUser.getInstance().getAuthToken(),
        device_id: window.app.deviceIdentifier,
        accountId: CurrentUser.getInstance().getAccountId(),
        account: CurrentUser.getInstance().getAccount()
    };

    if (window.app.config.deny_tutorial) {
        startData.deny_tutorial = 1;
    }

    if (window.app.config.suid_simulate) {
        startData.suid_simulate = window.app.config.suid_simulate;
    }

    if (window.app.config.flag_spoof) {
        startData.flag_spoof = 1;
    }
    return startData;
}

export async function cmdGetStartData(isRetry: boolean = false) {
    const GET_START_DATA_RETRY_DELAY = 100;

    let startData: Partial<GameStartData>;

    try {
        if (!isRetry) {
            logger.info('GET_START_DATA', 'start');
        }

        await isGetUserFinished();

        if (window.app.flags.user_ready && window.app.flags.get_user_ready) {
            startData = buildStartData();
            logger.info('set_start_data', startData);
            GameClient.getInstance().sendCommand<void>(new SetStartDataCommand(startData));
            logger.info('get_start_data', 'ready', startData);
        } else {
            if (typeof isRetry === 'undefined') {
                logger.info(
                    'get_start_data',
                    'not ready | retry',
                    window.app.flags.user_ready,
                    window.app.flags.get_user_ready
                );
            }
            setTimeout(function retryGetStartData() {
                cmdGetStartData(true);
            }, GET_START_DATA_RETRY_DELAY);
        }
    } catch (error) {
        logger.error(error);
        window.Sentry.captureException(error);
        GameClient.getInstance().sendCommand(new OnErrorCommand({
            error: (<Error>error).message,
            method: WebCommands.GET_START_DATA
        }));
    }
}
